import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { SUBSCRIPTION_PLANS } from '../../../constants/subscriptionPlans';
import MorafaaLogo from '../../../components/ui/MorafaaLogo';
import AnnouncementBar from '../../../components/AnnouncementBar';
import Footer from '../../../components/Footer';
import { trackLandingPageVisit } from '../../../services/statisticsService';
import AdSenseSlot from '../../../components/ads/AdSenseSlot';
import { 
  Calendar, 
  Users, 
  Building2, 
  FileText, 
  Shield, 
  Cloud, 
  Smartphone, 
  Globe, 
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  MessageSquare,
  Facebook,
  Instagram,
  Mail,
  Phone,
  Gift,
  UserCheck,
  Crown,
  Paperclip,
  CreditCard,
  Headphones,
  Clock10
} from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLocalization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isAnnouncementVisible, setIsAnnouncementVisible] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // Ref to track if we've already attempted to track this visit
  // Prevents duplicate tracking from React StrictMode double mounting
  const hasTrackedRef = useRef(false);

  // Track landing page visit from external sources
  // Wrapped in try-catch to ensure no errors affect user experience
  useEffect(() => {
    // Prevent duplicate tracking if useEffect runs twice (React StrictMode)
    if (hasTrackedRef.current) {
      return;
    }
    
    try {
      // Only track if this is a new page load (not a navigation within the site)
      // Check if referrer is external or if there's no referrer (direct visit)
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const referrer = document.referrer;
        const currentOrigin = window.location.origin;
        const isExternal = !referrer || !referrer.startsWith(currentOrigin);
        
        if (isExternal) {
          // Mark as tracked immediately to prevent duplicate calls
          hasTrackedRef.current = true;
          
          // Call tracking function - it handles all errors internally
          // Using void to explicitly ignore the promise result
          void trackLandingPageVisit();
        }
      }
    } catch (error) {
      // Ultimate safety net - ensure no errors bubble up
      // This should never happen as trackLandingPageVisit never throws,
      // but we add this as an extra layer of protection
      if (process.env.NODE_ENV === 'development') {
        console.warn('Statistics tracking initialization error (silent):', error);
      }
    }
  }, []); // Run only once on component mount

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'features', 'technical', 'platforms', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle contact form submission
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simple form validation
      if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
        setSubmitStatus('error');
        return;
      }

      // For now, just simulate a successful submission
      // In a real app, you would send this to your backend
      console.log('Contact form submitted:', {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        language: language
      });

      setSubmitStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const navigationItems = [
    { id: 'home', label: language === 'ar' ? 'الرئيسية' : 'Home' },
    { id: 'features', label: language === 'ar' ? 'المميزات' : 'Features' },
    { id: 'technical', label: language === 'ar' ? 'التقنيات' : 'Technical' },
    { id: 'platforms', label: language === 'ar' ? 'المنصات' : 'Platforms' },
    { id: 'pricing', label: language === 'ar' ? 'الأسعار' : 'Pricing' },
    { id: 'contact', label: language === 'ar' ? 'تواصل معنا' : 'Contact' },
  ];

  const appFeatures = [
    {
      icon: <Users className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'إدارة الموكلين' : 'Client Management',
      description: language === 'ar' 
        ? 'تنظيم وإدارة بيانات الموكلين مع إمكانية إنشاء ملف شخصي لكل موكل'
        : 'Organize and manage client data with the ability to create a personal file for each client',
      color: 'from-primary to-blue-600'
    },
    {
      icon: <Calendar className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'الأجندة والمواعيد' : 'Agenda & Appointments',
      description: language === 'ar' 
        ? 'تسجيل مواعيد الجلسات مع الإشعارات التلقائية'
        : 'Record session appointments with automatic notifications',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: <Building2 className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'المكاتب المتعددة' : 'Multiple Offices',
      description: language === 'ar' 
        ? 'إدارة مكاتب متعددة وربطها بالمواعيد والموكلين'
        : 'Manage multiple offices and link them to appointments and clients',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: <FileText className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'الملفات القانونية' : 'Legal Files',
      description: language === 'ar' 
        ? 'تنظيم وإدارة الملفات القانونية مع تتبع حالة كل ملف'
        : 'Organize and manage legal files with status tracking for each case',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: <Paperclip className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'مرفقات الملفات' : 'File Attachments',
      description: language === 'ar' 
        ? 'رفع وإدارة المستندات والصور والملفات المرفقة بكل ملف قانوني'
        : 'Upload and manage documents, images, and files attached to each legal case',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: <CreditCard className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'إدارة المدفوعات' : 'Payment Management',
      description: language === 'ar' 
        ? 'تتبع المدفوعات والإيرادات والمصروفات لكل ملف قانوني'
        : 'Track payments, income, and expenses for each legal case',
      color: 'from-violet-500 to-violet-600'
    },
    {
      icon: <Headphones className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'مركز الدعم' : 'Support Center',
      description: language === 'ar' 
        ? 'دعم فني متاح على مدار الساعة لمساعدتك في حل أي مشكلة'
        : '24/7 technical support available to help you resolve any issues',
      color: 'from-rose-500 to-rose-600'
    },
    {
    //   icon: <Gift className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
    //   title: language === 'ar' ? 'برنامج الإحالة' : 'Referral Program',
    //   description: language === 'ar' 
    //     ? 'احصل على مكافآت عند إحالة محامين جدد لمنصة مرافعة'
    //     : 'Earn rewards when you refer new lawyers to the Morafaa platform',
    //   color: 'from-red-500 to-red-600'
    // },
    // {
      icon: <UserCheck className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'دليل المحامي' : 'Lawyer Guide',
      description: language === 'ar' 
        ? 'دليل شامل للمحامين مع نصائح وإرشادات قانونية متخصصة'
        : 'Comprehensive guide for lawyers with specialized legal tips and guidance',
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: <MessageSquare className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'المجتمع' : 'Community',
      description: language === 'ar' 
        ? 'انضم لمجتمع المحامين وشارك الخبرات والاستشارات'
        : 'Join the lawyer community and share experiences and consultations',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: <Users className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'أعضاء الفريق' : 'Team Members',
      description: language === 'ar' 
        ? 'إدارة فريق العمل وتوزيع المهام والمشاريع القانونية'
        : 'Manage your team and distribute tasks and legal projects',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: <Crown className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'الإحصائيات المتقدمة' : 'Advanced Statistics',
      description: language === 'ar' 
        ? 'تقارير وإحصائيات مفصلة عن أداء المكتب والموكلين'
        : 'Detailed reports and statistics about office performance and clients',
      color: 'from-amber-500 to-amber-600'
    }
  ];

  const technicalFeatures = [
    {
      icon: <Cloud className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'التخزين السحابي' : 'Cloud Storage',
      description: language === 'ar' 
        ? 'تخزين آمن وموثوق لجميع بياناتك في السحابة'
        : 'Secure and reliable storage of all your data in the cloud'
    },
    {
      icon: <Smartphone className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'العمل بدون إنترنت' : 'Offline Mode',
      description: language === 'ar' 
        ? 'إمكانية العمل بدون اتصال بالإنترنت على الأجهزة المحمولة'
        : 'Work offline on mobile devices without internet connection'
    },
    {
      icon: <Globe className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'منصات متعددة' : 'Multi-platform',
      description: language === 'ar' 
        ? 'متوفر على الويب والأندرويد مع قريباً على iOS'
        : 'Available on Web and Android with iOS coming soon'
    },
    {
      icon: <Shield className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'أمان متقدم' : 'Advanced Security',
      description: language === 'ar' 
        ? 'حماية متقدمة للبيانات مع تشفير آمن'
        : 'Advanced data protection with secure encryption'
    },
    {
      icon: <Globe className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      title: language === 'ar' ? 'متعدد اللغات' : 'Multi-language',
      description: language === 'ar' 
        ? 'واجهة سهلة وبسيطة باللغتين العربية والإنجليزية'
        : 'Simple and easy-to-use interface in Arabic and English'
    }
  ];

  const platforms = [
    {
      name: 'Web',
      description: language === 'ar' ? 'المنصة الويب' : 'Web Platform',
      icon: <Globe className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      action: () => navigate('/login'),
      buttonText: language === 'ar' ? 'تسجيل الدخول' : 'Login',
      available: true,
      color: 'from-primary to-primary-dark'
    },
    {
      name: 'Android',
      description: language === 'ar' ? 'تطبيق الأندرويد' : 'Android App',
      icon: <Smartphone className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      action: () => window.open('https://play.google.com/store/apps/details?id=com.optimizeware.morafaa', '_blank'),
      buttonText: language === 'ar' ? 'تحميل من جوجل بلاي' : 'Download from Play Store',
      available: true,
      color: 'from-primary to-primary-dark'
    },
    {
      name: 'iOS',
      description: language === 'ar' ? 'تطبيق iOS' : 'iOS App',
      icon: <Smartphone className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center p-4 text-primary" />,
      action: () => {},
      buttonText: language === 'ar' ? 'قريباً' : 'Coming Soon',
      available: false,
      color: 'from-gray-400 to-gray-500'
    }
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      icon: <Facebook className="w-5 h-5 text-accent dark:text-accent-light" />,
      url: 'https://www.facebook.com/morafaaapp',
      color: 'hover:text-blue-600'
    },
    {
      name: 'Instagram',
      icon: <Instagram className="w-5 h-5 text-accent dark:text-accent-light" />,
      url: 'https://www.instagram.com/morafaaapp/',
      color: 'hover:text-pink-600'
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'dark' : ''}`}>
      {/* Header */}
      <header className={`fixed left-0 right-0 z-50 bg-primary dark:bg-primary-dark backdrop-blur-md border-b border-primary/20 dark:border-primary-light/20 ${isAnnouncementVisible ? 'top-12' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <MorafaaLogo size="md" variant="light" />
              <span className="text-xl font-bold text-white">
                {language === 'ar' ? 'مرافعة' : 'Morafaa'}
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    activeSection === item.id
                      ? 'text-accent'
                      : 'text-white/80 hover:text-accent'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Language & Theme Toggle */}
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="px-3 py-1 text-sm rounded-md transition-colors bg-white/20 text-white hover:bg-white/30"
              >
                {language === 'ar' ? 'English' : 'العربية'}
              </button>

              {/* Login Button */}
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-accent hover:bg-accent-light text-primary font-semibold rounded-lg transition-colors duration-200 flex items-center space-x-2 rtl:space-x-reverse"
              >
                <span>{language === 'ar' ? 'تسجيل الدخول' : 'Login'}</span>
                <ArrowRight className={`w-4 h-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-white/20 text-white hover:bg-white/30"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-primary/95 dark:bg-primary-dark/95 border-t border-primary/20 dark:border-primary-light/20">
            <div className="px-4 py-2 space-y-1">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'text-accent bg-white/20'
                      : 'text-white/80 hover:text-accent hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile Login Button */}
              <button
                onClick={() => {
                  navigate('/login');
                  setIsMenuOpen(false);
                }}
                className="w-full mt-4 px-4 py-2 bg-accent hover:bg-accent-light text-primary font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <span>{language === 'ar' ? 'تسجيل الدخول' : 'Login'}</span>
                <ArrowRight className={`w-4 h-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Announcement Bar */}
      <AnnouncementBar onHide={() => setIsAnnouncementVisible(false)} />

      {/* Main Content */}
      <main className={`${isAnnouncementVisible ? 'pt-28' : 'pt-16'}`}>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-primary/10 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <div className="flex justify-center mb-8">
                <MorafaaLogo size="xl" variant={isDark ? 'light' : 'dark'} />
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                {language === 'ar' ? (
                  <>
                    <span className="text-primary dark:text-primary-light">مرافعة</span>
                    <br />
                    لإدارة أعمال المحامين
                  </>
                ) : (
                  <>
                    <span className="text-primary dark:text-primary-light">Morafaa</span>
                    <br />
                    Legal Practice Management
                  </>
                )}
              </h1>
              
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'منصة شاملة لإدارة الممارسة القانونية - تنظيم الموكلين، الملفات القانونية، المواعيد، والمكاتب المتعددة'
                  : 'Comprehensive platform for legal practice management - organize clients, legal files, appointments, and multiple offices'
                }
              </p>

              {/* Features Overview Section - Horizontal Layout */}
              <div className="mb-12">
                <div className="max-w-7xl mx-auto">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Text Content */}
                    <div className={`${language === 'ar' ? 'lg:order-2' : 'lg:order-1'}`}>
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            {language === 'ar' ? 'منصة مرافعة' : 'Morafaa Platform'}
                          </h3>
                          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                            {language === 'ar' 
                              ? 'منصة شاملة تجمع كل ما تحتاجه لإدارة ممارستك القانونية بكفاءة واحترافية. استمتع بإدارة العملاء، الملفات القانونية، المواعيد، والمكاتب المتعددة في مكان واحد.'
                              : 'A comprehensive platform that brings together everything you need to manage your legal practice efficiently and professionally. Enjoy managing clients, legal files, appointments, and multiple offices all in one place.'
                            }
                          </p>
                        </div>

                        {/* Key Features List */}
                        <div className="space-y-4">
                          <div className="flex items-start space-x-3 rtl:space-x-reverse">
                            <div className="w-6 h-6 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Users className="w-4 h-4 text-primary dark:text-primary-light" />
                            </div>
                            <div 
                              className="flex-1"
                              style={{ textAlign: language === 'ar' ? 'right' : 'left' }}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {language === 'ar' ? 'إدارة العملاء المتقدمة' : 'Advanced Client Management'}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300 text-sm">
                                {language === 'ar' 
                                  ? 'تنظيم وإدارة بيانات العملاء والملفات القانونية بسهولة'
                                  : 'Organize and manage client data and legal files with ease'
                                }
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3 rtl:space-x-reverse">
                            <div className="w-6 h-6 bg-accent/10 dark:bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FileText className="w-4 h-4 text-accent dark:text-accent-light" />
                            </div>
                            <div 
                              className="flex-1"
                              style={{ textAlign: language === 'ar' ? 'right' : 'left' }}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {language === 'ar' ? 'تتبع الملفات القانونية' : 'Legal File Tracking'}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300 text-sm">
                                {language === 'ar' 
                                  ? 'تتبع وإدارة جميع الملفات والمستندات القانونية'
                                  : 'Track and manage all legal files and documents'
                                }
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3 rtl:space-x-reverse">
                            <div className="w-6 h-6 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Calendar className="w-4 h-4 text-primary dark:text-blue-400" />
                            </div>
                            <div 
                              className="flex-1"
                              style={{ textAlign: language === 'ar' ? 'right' : 'left' }}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {language === 'ar' ? 'جدولة المواعيد الذكية' : 'Smart Appointment Scheduling'}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300 text-sm">
                                {language === 'ar' 
                                  ? 'جدولة وإدارة المواعيد والجلسات بذكاء'
                                  : 'Intelligently schedule and manage appointments and sessions'
                                }
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3 rtl:space-x-reverse">
                            <div className="w-6 h-6 bg-green-500/10 dark:bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Building2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            </div>
                            <div 
                              className="flex-1"
                              style={{ textAlign: language === 'ar' ? 'right' : 'left' }}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {language === 'ar' ? 'إدارة المكاتب المتعددة' : 'Multi-Office Management'}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300 text-sm">
                                {language === 'ar' 
                                  ? 'إدارة عدة مكاتب وفروع بسهولة'
                                  : 'Manage multiple offices and branches easily'
                                }
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Additional Benefits */}
                        <div className="flex flex-wrap gap-4 pt-4">
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <Shield className="w-5 h-5 text-primary dark:text-primary-light" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {language === 'ar' ? 'أمان عالي' : 'High Security'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <Cloud className="w-5 h-5 text-accent dark:text-accent-light" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {language === 'ar' ? 'تخزين سحابي' : 'Cloud Storage'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <Smartphone className="w-5 h-5 text-primary dark:text-blue-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {language === 'ar' ? 'متوفر على جميع الأجهزة' : 'All Devices'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Image Content */}
                    <div className={`${language === 'ar' ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="relative">
                        <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                          <img
                            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                            alt={language === 'ar' ? 'منصة مرافعة لإدارة الممارسة القانونية' : 'Morafaa Legal Practice Management Platform'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Decorative Elements */}
                        <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-xl"></div>
                        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-xl"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/signup')}
                  className="px-8 py-4 bg-accent hover:bg-accent-light text-primary font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse"
                >
                  <span>{language === 'ar' ? 'ابدأ الآن' : 'Get Started'}</span>
                  <ArrowRight className={`w-5 h-5 ${language === 'ar' ? 'rotate-180' : ''}`} />
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className="px-8 py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary-light transition-colors duration-200"
                >
                  {language === 'ar' ? 'اكتشف المميزات' : 'Explore Features'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Features Section */}
        <section id="features" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'مميزات المنصة' : 'Platform Features'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'كل ما تحتاجه لإدارة ممارستك القانونية بكفاءة وأمان'
                  : 'Everything you need to manage your legal practice efficiently and securely'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {appFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-200 dark:border-gray-700 text-center"
                >
                  <div className="w-16 h-16 rounded-xl bg-primary dark:bg-primary-dark flex items-center justify-center mb-6 mx-auto">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Features Section */}
        <section id="technical" className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'المميزات التقنية' : 'Technical Features'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'تقنيات متقدمة لضمان الأداء والأمان والموثوقية'
                  : 'Advanced technologies to ensure performance, security, and reliability'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {technicalFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="text-center bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms Section */}
        <section id="platforms" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'متاح على جميع المنصات' : 'Available on All Platforms'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'استخدم مرافعة على أي جهاز، في أي مكان، وفي أي وقت'
                  : 'Use Morafaa on any device, anywhere, anytime'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {platforms.map((platform, index) => (
                <div
                  key={index}
                  className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                    platform.available 
                      ? 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600' 
                      : 'border-gray-300 dark:border-gray-600 opacity-75'
                  }`}
                >
                  {!platform.available && (
                    <div className="absolute top-4 right-4 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-3 py-1 rounded-full text-sm font-medium">
                      {language === 'ar' ? 'قريباً' : 'Coming Soon'}
                    </div>
                  )}
                  
                  <div className="w-16 h-16 mx-auto mb-6 bg-primary dark:bg-primary-dark rounded-xl flex items-center justify-center">
                    {platform.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    {platform.name}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                    {platform.description}
                  </p>
                  
                  <button
                    onClick={platform.action}
                    disabled={!platform.available}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      platform.available
                        ? `bg-gradient-to-r ${platform.color} text-white hover:shadow-lg transform hover:scale-105`
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {platform.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'الأسعار' : 'Pricing'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'أسعار بسيطة وعادلة تناسب جميع أحجام المكاتب القانونية'
                  : 'Simple and fair pricing that fits all law firm sizes'
                }
              </p>
            </div>

            <div className="max-w-7xl mx-auto">
              {/* Single Pricing Plan */}
              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/20 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/20 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-3xl font-bold mb-4">
                      {language === 'ar' ? 'خطط تناسب كل احتياج' : 'Plans That Fit Every Need'}
                    </h3>
                    <p className="text-lg text-white/90 mb-6">
                      {language === 'ar' 
                        ? 'ابدأ مجاناً وارتقِ حسب نمو ممارستك القانونية'
                        : 'Start free and scale as your legal practice grows'
                      }
                    </p>
                  </div>

                  {/* Three-Tier Pricing Structure */}
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {SUBSCRIPTION_PLANS.map((plan, index) => {
                      const getPlanIcon = () => {
                        switch (plan.icon) {
                          case 'gift': return <Users className="w-6 h-6 text-primary" />;
                          case 'star': return <UserCheck className="w-6 h-6 text-primary" />;
                          case 'crown': return <Crown className="w-6 h-6 text-primary" />;
                          default: return <Users className="w-6 h-6 text-primary" />;
                        }
                      };

                      const formatLimit = (limit: number) => {
                        return limit === -1 ? (language === 'ar' ? 'غير محدود' : 'Unlimited') : limit.toString();
                      };

                      return (
                        <div 
                          key={plan.id}
                          className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 relative ${
                            plan.popular ? 'border-2 border-accent' : ''
                          }`}
                        >
                          {/* Popular Badge */}
                          {plan.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-accent text-primary px-4 py-1 rounded-full text-sm font-semibold">
                                {language === 'ar' ? 'الأكثر شعبية' : 'Most Popular'}
                              </span>
                            </div>
                          )}

                          {/* Plan Icon */}
                          <div className="flex items-center justify-center mb-4">
                            <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                              {getPlanIcon()}
                            </div>
                          </div>

                          {/* Plan Name */}
                          <h4 className="text-xl font-semibold mb-2 text-center">
                            {language === 'ar' ? plan.nameAr : plan.name}
                          </h4>

                          {/* Pricing */}
                          <div className="text-center">
                            <span className="text-4xl font-bold">
                              {plan.isFreeTier ? '0' : plan.price}
                            </span>
                            {!plan.isFreeTier && (
                              <span className="text-lg ml-2">{plan.currency}</span>
                            )}
                            <span className="block text-sm text-white/80 mt-1">
                              {language === 'ar' 
                                ? (plan.isFreeTier ? 'مجاني' : plan.billingCycle === 'monthly' ? 'شهرياً' : 'سنوياً')
                                : (plan.isFreeTier ? 'Free' : `per ${plan.billingCycle}`)
                              }
                            </span>
                          </div>

                          {/* Description */}
                          <p className="text-sm text-white/80 text-center mt-3">
                            {language === 'ar' ? plan.descriptionAr : plan.description}
                          </p>

                          {/* Features */}
                          <div className="mt-4 space-y-2">
                            {/* Limits */}
                            <div className="flex items-center text-sm text-white/90">
                              <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                              <span>
                                {language === 'ar' 
                                  ? `${formatLimit(plan.maxOffices)} ${plan.maxOffices === -1 ? 'مكتب' : 'مكتب'}`
                                  : `${formatLimit(plan.maxOffices)} ${plan.maxOffices === -1 ? 'Offices' : 'Office' + (plan.maxOffices !== 1 ? 's' : '')}`
                                }
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-white/90">
                              <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                              <span>
                                {language === 'ar' 
                                  ? `${formatLimit(plan.maxClientsTotal)} ${plan.maxClientsTotal === -1 ? 'عميل' : 'عميل'} ${plan.maxClientsTotal === -1 ? '' : 'إجمالي'}`
                                  : `${formatLimit(plan.maxClientsTotal)} ${plan.maxClientsTotal === -1 ? 'Clients' : 'Client' + (plan.maxClientsTotal !== 1 ? 's' : '')} ${plan.maxClientsTotal === -1 ? '' : 'Total'}`
                                }
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-white/90">
                              <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                              <span>
                                {language === 'ar' 
                                  ? `${formatLimit(plan.maxFilesTotal)} ${plan.maxFilesTotal === -1 ? 'ملف' : 'ملف'} ${plan.maxFilesTotal === -1 ? '' : 'إجمالي'}`
                                  : `${formatLimit(plan.maxFilesTotal)} ${plan.maxFilesTotal === -1 ? 'Files' : 'File' + (plan.maxFilesTotal !== 1 ? 's' : '')} ${plan.maxFilesTotal === -1 ? '' : 'Total'}`
                                }
                              </span>
                            </div>

                            {/* Premium Features */}
                            {plan.includesEventReminders && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>{language === 'ar' ? 'تذكيرات الأحداث' : 'Event Reminders'}</span>
                              </div>
                            )}
                            {plan.includesAdvancedStats && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>{language === 'ar' ? 'إحصائيات متقدمة' : 'Advanced Statistics'}</span>
                              </div>
                            )}
                            {plan.includesCommunity && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>{language === 'ar' ? 'المجتمع' : 'Community'}</span>
                              </div>
                            )}
                            {plan.includesPhoneSupport && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>{language === 'ar' ? 'دعم هاتفي' : 'Phone Support'}</span>
                              </div>
                            )}
                            {plan.includesExportExcel && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>{language === 'ar' ? 'تصدير إكسل' : 'Export Excel'}</span>
                              </div>
                            )}
                            {plan.teamMemberPrice && (
                              <div className="flex items-center text-sm text-white/90">
                                <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                                <span>
                                  {language === 'ar' 
                                    ? `${plan.teamMemberPrice} جنيه لكل عضو فريق`
                                    : `${plan.teamMemberPrice} ${plan.currency} per team member`
                                  }
                                </span>
                              </div>
                            )}
                          </div>

                          {/* CTA Button */}
                          <div className="mt-6">
                            <button
                              onClick={() => navigate('/signup')}
                              className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 ${
                                plan.popular
                                  ? 'bg-accent hover:bg-accent-light text-primary'
                                  : 'bg-white/20 hover:bg-white/30 text-white'
                              }`}
                            >
                              {language === 'ar' ? 'ابدأ الآن' : 'Get Started'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* All Plans Include */}
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8">
                    <h4 className="text-lg font-semibold mb-4 text-center">
                      {language === 'ar' ? 'جميع الخطط تشمل' : 'All Plans Include'}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'إدارة العملاء' : 'Client Management'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'إدارة المكاتب' : 'Office Management'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'إدارة الأحداث' : 'Event Management'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'الملفات القانونية' : 'Legal Files'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'دليل المحامي' : 'Lawyer Guide'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'التخزين السحابي' : 'Cloud Storage'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                        <span className="text-white/90">
                          {language === 'ar' ? 'الدعم الفني' : 'Technical Support'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="text-center">
                    <button
                      onClick={() => navigate('/signup')}
                      className="bg-accent hover:bg-accent-light text-primary font-semibold py-4 px-8 rounded-xl transition-colors duration-200 mb-4"
                    >
                      {language === 'ar' ? 'ابدأ الآن' : 'Get Started Now'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Why This Pricing Works */}
              <div className="mt-16 grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-primary dark:text-primary-light" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {language === 'ar' ? 'ابدأ مجاناً' : 'Start Free'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {language === 'ar' 
                      ? 'جرب الخدمة مجاناً وارتقِ حسب احتياجاتك'
                      : 'Try the service for free and upgrade as you grow'
                    }
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-primary dark:text-primary-light" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {language === 'ar' ? 'خطط مرنة' : 'Flexible Plans'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {language === 'ar' 
                      ? 'اختر الخطة التي تناسب حجم ممارستك القانونية'
                      : 'Choose the plan that fits your practice size'
                    }
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-primary dark:text-primary-light" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {language === 'ar' ? 'مميزات متقدمة' : 'Advanced Features'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {language === 'ar' 
                      ? 'احصل على مميزات حصرية مع الخطط المدفوعة'
                      : 'Get exclusive features with paid plans'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {language === 'ar' 
                  ? 'نحن هنا لمساعدتك في أي وقت'
                  : 'We are here to help you anytime'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center">
                    <Mail className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center p-3 lucide-users w-12 h-12 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">morafaaapp@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center">
                    <Phone className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center p-3 lucide-users w-12 h-12 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'ar' ? 'الهاتف' : 'Phone'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      <a 
                        href="https://wa.me/201202322241" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-green-600 dark:hover:text-green-400 transition-colors"
                      >
                        +201202322241 (WhatsApp)
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center">
                    <Clock10 className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center p-3 lucide-users w-12 h-12 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'ar' ? 'ساعات العمل' : 'Working Hours'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {language === 'ar' ? 'السبت - الخميس: 9:00 ص - 6:00 م' : 'Saturday - Thursday: 9:00 AM - 6:00 PM'}
                    </p>
                  </div>
                </div>

                {/* Social Media Links */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {language === 'ar' ? 'تابعنا على' : 'Follow Us'}
                  </h3>
                  <div className="flex space-x-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        className={`w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-accent dark:text-accent-light transition-colors duration-200 ${social.color}`}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                    <MessageSquare className="w-6 h-6 text-accent dark:text-accent-light" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {language === 'ar' ? 'أرسل لنا رسالة' : 'Send us a message'}
                  </h3>
                </div>
                
                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      {language === 'ar' 
                        ? 'تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.'
                        : 'Your message has been sent successfully! We will contact you soon.'
                      }
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
                    <div className="flex items-center">
                      <X className="w-5 h-5 mr-2" />
                      {language === 'ar' 
                        ? 'حدث خطأ في إرسال الرسالة. يرجى المحاولة مرة أخرى.'
                        : 'An error occurred while sending the message. Please try again.'
                      }
                    </div>
                  </div>
                )}

                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'ar' ? 'الاسم' : 'Name'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-white"
                      placeholder={language === 'ar' ? 'أدخل اسمك' : 'Enter your name'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-white"
                      placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'ar' ? 'الرسالة' : 'Message'}
                    </label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-white"
                      placeholder={language === 'ar' ? 'أدخل رسالتك' : 'Enter your message'}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary-dark disabled:bg-primary/50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}</span>
                      </>
                    ) : (
                      <span>{language === 'ar' ? 'إرسال الرسالة' : 'Send Message'}</span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* OW-172 — approved public surface only; suppressed when Ad-Free active */}
      <AdSenseSlot surface="landing" />

      <Footer />
    </div>
  );
};

export default LandingPage;
